import { ITEM_TYPES } from '../constants';
import { debtBalanceAt, itemMonthlyCashflow, liquidMonthlyInterest, monthlyAmount, num } from './engine';

// Dashboard-level aggregates: the portfolio *as it stands today*, with no month
// loop and no projection. The per-item arithmetic comes from the kernel, so
// these KPIs cannot drift from the chart's t0.

export const calculateItemAnnualAppreciationValue = (item) => {
    const currentValue = num(item.currentValue);
    if (currentValue === 0) return 0;

    switch (item.type) {
        case ITEM_TYPES.REAL_ESTATE:
            return currentValue * num(item.annualAppreciationRate);
        case ITEM_TYPES.OTHER_INVESTMENT:
            return item.isGrowth ? currentValue * num(item.annualGrowthRate) : 0;
        default:
            return 0;
    }
};

export const calculateRealEstateReturnOnEquity = (item) => {
    if (item.type !== ITEM_TYPES.REAL_ESTATE || !item.hasMortgage) return 0;

    const currentValue = num(item.currentValue);
    const mortgageInitialAmount = num(item.mortgageInitialAmount);
    // Equity here is against the ORIGINAL mortgage, not today's balance: this
    // answers "what did my own money earn", so the deposit is the denominator.
    const equity = currentValue - mortgageInitialAmount;
    if (currentValue <= 0 || mortgageInitialAmount < 0 || equity <= 0) return 0;

    return (currentValue * num(item.annualAppreciationRate)) / equity;
};

// Gross income, not net: this is the denominator of the savings rate, so it
// counts what comes in and ignores what goes out. A withdrawal from an
// investment counts as income; a contribution is not counted as negative.
export const calculateTotalAnnualIncome = (activeItems, liquidAssets, liquidAssetsInterestRate) => {
    let totalAnnualIncome = 0;

    if (liquidAssets > 0 && liquidAssetsInterestRate > 0) {
        totalAnnualIncome += liquidAssets * liquidAssetsInterestRate;
    }

    activeItems.forEach(item => {
        let monthlyIncome = 0;
        switch (item.type) {
            case ITEM_TYPES.INCOME:
                monthlyIncome = monthlyAmount(item.monthlyIncome, item.amountFrequency);
                break;
            case ITEM_TYPES.REAL_ESTATE:
                if (item.isRented) {
                    monthlyIncome = monthlyAmount(item.monthlyRentalIncome, item.amountFrequency);
                }
                break;
            case ITEM_TYPES.OTHER_INVESTMENT: {
                if (item.isIncome) {
                    monthlyIncome += (num(item.currentValue) * num(item.annualIncomeYield)) / 12;
                }
                const contribution = monthlyAmount(item.monthlyContribution, item.contributionFrequency);
                if (contribution < 0) monthlyIncome += Math.abs(contribution);
                break;
            }
            default:
                break;
        }
        totalAnnualIncome += monthlyIncome * 12;
    });

    return totalAnnualIncome;
};

export const calculateAllAnalytics = (activeItems, liquidAssets, liquidAssetsInterestRate) => {
    const isAsset = (item) => item.type === ITEM_TYPES.REAL_ESTATE || item.type === ITEM_TYPES.OTHER_INVESTMENT;
    const investedAssetsValue = activeItems.reduce((sum, item) => isAsset(item) ? sum + num(item.currentValue) : sum, 0);

    const totalCurrentDebts = activeItems.reduce((sum, item) => sum + debtBalanceAt(item), 0);

    const netWorth = investedAssetsValue - totalCurrentDebts;
    const grossWorth = investedAssetsValue;

    const portfolioCashflow = activeItems.reduce((sum, item) => sum + itemMonthlyCashflow(item), 0);
    const liquidAssetsNum = num(liquidAssets);
    const totalMonthlyCashflow = portfolioCashflow + liquidMonthlyInterest(liquidAssetsNum, liquidAssetsInterestRate);

    const totalAnnualAppreciation = activeItems.reduce((sum, item) => sum + calculateItemAnnualAppreciationValue(item), 0);

    // Yield is measured against net worth — return on the capital actually at
    // risk, not on the gross value of assets bought partly with debt.
    const effectivePortfolioYield = (netWorth <= 0 || totalAnnualAppreciation === 0) ? 0 : totalAnnualAppreciation / netWorth;

    const totalAssets = investedAssetsValue + liquidAssetsNum;
    const equityRatio = totalAssets === 0 ? 0 : (totalAssets - totalCurrentDebts) / totalAssets;

    return { netWorth, grossWorth, totalMonthlyCashflow, totalAnnualAppreciation, effectivePortfolioYield, equityRatio };
};
